// Phase 49: Date range helpers for batch transaction apply.
//
// expandDateRange(start, end, daysOfWeek) generates an array of
// 'YYYY-MM-DD' strings for every date in [start, end] that falls on one
// of the listed weekdays. Pure function — no React, no IndexedDB, so
// it's straightforward to unit-test.

/**
 * Expand a date range into individual occurrence dates.
 *
 * @param start    'YYYY-MM-DD' — inclusive start of the range
 * @param end      'YYYY-MM-DD' — inclusive end of the range
 * @param daysOfWeek Array of integers 0..6 (Sun=0, Mon=1, ..., Sat=6).
 *                   If empty/null/undefined, every day in the range is included.
 * @returns {string[]} Array of 'YYYY-MM-DD' strings, sorted chronologically.
 *
 * Edge cases:
 *   - start > end → returns []
 *   - start === end and day-of-week doesn't match → returns []
 *   - daysOfWeek contains invalid integers (e.g. -1 or 7) → ignored
 *
 * Local-time: dates are interpreted at midnight local time, so DST
 * transitions don't shift the result. (Adding 24h per iteration is
 * more reliable than `new Date(d.getTime() + 86400000)` because the
 * latter can land on the same day twice in DST spring-forward.)
 */
export function expandDateRange(start, end, daysOfWeek = null) {
  if (!start || !end || start > end) return [];
  const validDays = Array.isArray(daysOfWeek)
    ? daysOfWeek.filter(d => Number.isInteger(d) && d >= 0 && d <= 6)
    : null;

  const result = [];
  const cursor = new Date(start + 'T00:00:00');
  const last = new Date(end + 'T00:00:00');
  // Safety cap — refuse to expand more than 366 days (a full year + 1).
  // Prevents UI hangs from accidental 2025-01-01 → 2099-12-31 ranges.
  const cap = 366;
  let n = 0;
  while (cursor <= last && n < cap) {
    const dow = cursor.getDay(); // 0 = Sun, 6 = Sat
    if (validDays === null || validDays.includes(dow)) {
      result.push(toYmd(cursor));
    }
    // Advance one calendar day using local-time math (avoids DST issues).
    cursor.setDate(cursor.getDate() + 1);
    n++;
  }
  return result;
}

function toYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Default daysOfWeek for the "every weekday" preset (Mon-Fri).
 * Convenience constant exported for the UI.
 */
export const WEEKDAY_PRESETS = {
  every_day:   [0, 1, 2, 3, 4, 5, 6],
  weekdays:    [1, 2, 3, 4, 5],
  weekends:    [0, 6],
  mon_wed_fri: [1, 3, 5],
  tue_thu:     [2, 4],
};

/**
 * Display labels for the seven weekdays, indexed 0..6 (Sun..Sat).
 * Used by the UI for the day-of-week checkboxes.
 */
export const WEEKDAY_LABELS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const WEEKDAY_LABELS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];